import { useEffect, useState } from 'react'
import Chart from 'chart.js/auto'
import { PriceData } from '../../types/charts'
import { Series, SeriesGroup, SeriesOptions } from '../../utils/series'
import { ChartConfiguration, Plugin } from 'chart.js'
import { Box, Typography, useTheme } from '@mui/material'
import { lang } from '../../utils/lang'
import { Currency } from '../../types/models'
import { ChartSummary } from './ChartSummary'
import { sprintf } from 'sprintf-js'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'

const FORECAST_COLOR = '#B8860B'

export interface PriceHistoryChartProps {
  id: string
  prices: PriceData[]
  currency: Currency
  options: SeriesOptions
}

export function PriceHistoryChart(props: PriceHistoryChartProps): React.ReactElement {
  const { id, prices, currency, options } = props
  const theme = useTheme()
  const { t } = useTranslation()
  const [main, setMain] = useState('')
  const [left, setLeft] = useState('')
  const [right, setRight] = useState('')
  const { vat } = options

  const hasForecast = prices.some(p => p.forecast)
  const isQuarterScale = options.steps === 'quarter'

  // Update values for summary.
  const setGroup = (result:number, group: SeriesGroup | undefined): void => {
    if (!group) {
      setMain('—')
      setLeft('')
      setRight('')
    } else {
      if (result === null || result === undefined) {
        setMain('—')
      } else {
        setMain(`${sprintf('%.1f', result * (1 + (vat || 0)) / 10).replace('.', ',')}`)
      }
      setRight(lang.timeLabel(group.from, options.steps || 'hour'))
      switch (options.steps) {
        case 'day':
          setLeft(t('Daily average'))
          break
        case 'week':
          setLeft(t('Weekly average'))
          break
        case 'month':
          setLeft(t('Monthly average'))
          break
        case 'year':
          setLeft(t('Yearly average'))
          break
        default:
          setLeft('')
      }
    }
  }

  useEffect(() => {
    const { labels, result, groups, startIndex, endIndex } = Series.simplify(prices, options)
    const usedLabels: string[] = labels as string[]
    const timeKey = options.timeKey || 'timestamp'

    const usedData: (number | null)[] = result.map(e => e * (1 + (vat || 0)) / 10)

    // Build forecast flag per group by checking if any member's timestamp
    // matches a forecast price in the original data.
    const forecastTimes = new Set(
      prices.filter(p => p.forecast).map(p => DateTime.fromISO(p.from).toMillis())
    )
    const isForecastGroup = groups.map(g =>
      g.members.some((m: any) => forecastTimes.has(m[timeKey]))
    )

    // Helper to find the current midpoint and refresh the summary values.
    const refreshSummary = (chart: Chart): void => {
      const { min, max } = chart.scales.x
      const idx = Math.floor((min + max) / 2)
      setGroup(result[idx], groups[idx])
    }

    if (options.panX) {
      for (let i = 0; i < 24; i++) {
        usedLabels.push('')
        usedData.push(null)
        isForecastGroup.push(false)
      }
    }

    // For quarter scale: replace labels with hour numbers at :00, empty for :15/:30/:45
    if (isQuarterScale) {
      for (let i = 0; i < usedLabels.length; i++) {
        if (i < groups.length && groups[i]) {
          const dt = DateTime.fromMillis(groups[i].from).toLocal()
          usedLabels[i] = dt.minute === 0 ? String(dt.hour) : ''
        } else {
          usedLabels[i] = ''
        }
      }
    }

    if (options.steps === 'hour') {
      for (let i = 0; i < usedLabels.length; i++) {
        if (usedLabels[i] && parseInt(usedLabels[i]) % 2) {
          usedLabels[i] = ''
        }
      }
    }

    // Per-point colors: brown for forecast, yellow for confirmed
    const primaryColor = theme.palette.primary.main
    const bgColors = hasForecast
      ? usedData.map((_, i) => isForecastGroup[i] ? FORECAST_COLOR : primaryColor)
      : primaryColor
    const borderColors = hasForecast
      ? usedData.map((_, i) => isForecastGroup[i] ? FORECAST_COLOR : primaryColor)
      : primaryColor

    // Hour bands plugin for quarter scale
    const plugins: Plugin[] = [
      {
        id: 'refreshSummary',
        afterRender: (chart) => refreshSummary(chart)
      }
    ]

    if (isQuarterScale) {
      plugins.push({
        id: 'hourBands',
        beforeDraw: (chart) => {
          const { ctx, chartArea, scales } = chart
          if (!chartArea) return
          const xScale = scales.x

          // Walk through groups to find hour boundaries
          let currentHour = -1
          let hourIndex = 0
          let bandStart = -1

          for (let i = 0; i < groups.length; i++) {
            const dt = DateTime.fromMillis(groups[i].from).toLocal()
            if (dt.hour !== currentHour) {
              // Draw previous odd-hour band
              if (hourIndex % 2 === 1 && bandStart >= 0) {
                const stepPx = xScale.getPixelForValue(1) - xScale.getPixelForValue(0)
                const x1 = xScale.getPixelForValue(bandStart) - stepPx / 2
                const x2 = xScale.getPixelForValue(i - 1) + stepPx / 2
                ctx.save()
                ctx.fillStyle = 'rgba(255, 255, 255, 0.04)'
                ctx.fillRect(x1, chartArea.top, x2 - x1, chartArea.bottom - chartArea.top)
                ctx.restore()
              }
              currentHour = dt.hour
              hourIndex++
              bandStart = i
            }
          }
          // Draw last band if odd
          if (hourIndex % 2 === 1 && bandStart >= 0 && groups.length > 0) {
            const stepPx = xScale.getPixelForValue(1) - xScale.getPixelForValue(0)
            const x1 = xScale.getPixelForValue(bandStart) - stepPx / 2
            const x2 = xScale.getPixelForValue(groups.length - 1) + stepPx / 2
            ctx.save()
            ctx.fillStyle = 'rgba(255, 255, 255, 0.04)'
            ctx.fillRect(x1, chartArea.top, x2 - x1, chartArea.bottom - chartArea.top)
            ctx.restore()
          }
        }
      })
    }

    const config: ChartConfiguration = {
      type: options.type ? options.type : 'bar',
      plugins,

      options: {
        layout: {
          padding: {
            top: 20
          }
        },

        plugins: {
          tooltip: {
            enabled: false
          },
          legend: {
            display: false
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x',
              onPanComplete: ({ chart }) => refreshSummary(chart)
            },
          }
        },

        animation: false,

        scales: {
          x: {
            // Could do elswhere, but the indexing is off due to adjusted end timestamp.
            min: startIndex + 1,
            max: endIndex + 1,
            grid: {
              display: false
            },
            ticks: {
              color: theme.palette.grey[400],
              maxRotation: 0,
              autoSkip: isQuarterScale ? false : undefined,
              font: {
                size: 10
              },
            },
          },
          y: {
            suggestedMin: 0,
            suggestedMax: 25,
            border: {
              display: false,
              dash: [6, 12],
              dashOffset: 6,
            },
            grid: {
              color: '#dfdfdf',
              lineWidth: 0.175,
            },
            ticks: {
              color: theme.palette.grey[400],
              maxRotation: 0,
              callback: (value) => lang.moneyShort(value as number, currency)
            }
          }
        },
      },

      data: {
        labels: usedLabels,
        datasets: [
          {
            type: options.type,
            pointRadius: 0,
            borderWidth: options.type === 'line' ? 2 : 0,
            borderRadius: 4,
            backgroundColor: bgColors,
            borderColor: borderColors,
            data: usedData,
            tension: 0.3,
            animations: { tension: { easing: 'linear' } }
          }
        ]
      },
    }

    const myChart = new Chart(
      document.getElementById(id) as HTMLCanvasElement,
      config
    )

    return () => {
      myChart.destroy()
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, prices, theme.palette.primary.main, theme.palette.grey, currency, options])

  return (
    <Box>
      <ChartSummary main={main} unit="c/kWh" left={left} right={right}/>
      <canvas id={id}/>
      {hasForecast && (
        <Box sx={{ mt: 1, px: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 24, height: 2, backgroundColor: theme.palette.primary.main, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: theme.palette.grey[400], fontSize: '0.7rem' }}>
              {t('Confirmed spot prices')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 24, height: 2, backgroundColor: FORECAST_COLOR, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: theme.palette.grey[400], fontSize: '0.7rem' }}>
              {t('Spot price forecast')}
            </Typography>
          </Box>
        </Box>
      )}
   </Box>
  )
}
